import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock, Ticket, Hourglass, ScanLine, AlertTriangle, ChevronRight, PlayCircle, StopCircle,
} from "lucide-react";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import EstadoEventoBadge from "@/components/common/EstadoEventoBadge";
import { SCREENS, ADMIN_DASHBOARD, DASHBOARD_KPIS } from "@/constants/testIds";
import { obtenerKpisDashboard } from "@/data/dashboardKpisStore";

const formatearFechaCortaISO = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = String(iso).split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return iso;
    return new Date(y, m - 1, d).toLocaleDateString("es-MX", {
      day: "2-digit", month: "short",
    });
  } catch { return iso; }
};

function KpiCard({ testId, icon: Icon, label, value, hint, onClick, tone = "default" }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={!clickable}
      className={`group text-left rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft flex flex-col gap-3 transition-all ${clickable ? "hover:shadow-md hover:border-border" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="overline text-muted-foreground">{label}</span>
        <div className={`w-9 h-9 rounded-xl grid place-items-center ${tone === "warning" ? "bg-destructive/10 ring-1 ring-destructive/30 text-destructive" : "bg-accent/10 ring-1 ring-accent/30 text-accent"}`}>
          <Icon className="w-4 h-4" strokeWidth={1.7} />
        </div>
      </div>
      <p className="font-heading text-4xl leading-none tracking-tight">{value}</p>
      {hint && <p className="text-xs text-muted-foreground leading-relaxed flex items-center gap-1">
        {hint}
        {clickable && <ChevronRight className="w-3 h-3 opacity-60 group-hover:opacity-100" />}
      </p>}
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [errorKpis, setErrorKpis] = useState("");

  const cargar = useCallback(async () => {
    const data = await obtenerKpisDashboard();
    if (data && !data.error) {
      setKpis(data);
      setErrorKpis("");
    } else {
      setErrorKpis(
        data?.error === "network"
          ? "Sin conexión con el servidor. Reintenta más tarde."
          : "No se pudo obtener el resumen del día."
      );
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const proximos = useMemo(() => kpis?.eventosProximosPreview || [], [kpis]);

  return (
    <div data-testid={SCREENS.adminDashboard} className="flex flex-col gap-10">
      <AdminPageHeader eyebrow="Centro de operación" title="Dashboard" description="Sólo información accionable para operar el día." />

      {errorKpis && (
        <div
          data-testid={DASHBOARD_KPIS.errorCarga}
          className="rounded-lg border hairline bg-destructive/5 text-destructive px-3 py-2 text-xs flex items-center gap-2"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> {errorKpis}
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        <KpiCard
          testId={DASHBOARD_KPIS.ventaInicia}
          icon={PlayCircle}
          label="Venta inicia hoy"
          value={kpis?.ventaIniciaHoy ?? 0}
          hint={(kpis?.ventaIniciaHoy || 0) > 0 ? "Abrir listado de eventos" : "Sin eventos que abran venta hoy"}
          onClick={(kpis?.ventaIniciaHoy || 0) > 0 ? () => navigate("/admin/eventos?f=venta_activa") : undefined}
          tone={(kpis?.ventaIniciaHoy || 0) > 0 ? "warning" : "default"}
        />
        <KpiCard
          testId={DASHBOARD_KPIS.ventaTermina}
          icon={StopCircle}
          label="Venta termina hoy"
          value={kpis?.ventaCierraHoy ?? 0}
          hint={(kpis?.ventaCierraHoy || 0) > 0 ? "Revisar eventos" : "Sin cierres de venta hoy"}
          onClick={(kpis?.ventaCierraHoy || 0) > 0 ? () => navigate("/admin/eventos?f=venta_activa") : undefined}
          tone={(kpis?.ventaCierraHoy || 0) > 0 ? "warning" : "default"}
        />
        <KpiCard
          testId={DASHBOARD_KPIS.boletosPendientes}
          icon={Hourglass}
          label="Boletos sin asignar"
          value={kpis?.boletosSinAsignar ?? 0}
          hint="Total en la base de datos"
          tone={(kpis?.boletosSinAsignar || 0) > 0 ? "warning" : "default"}
        />
        <KpiCard
          testId={DASHBOARD_KPIS.accesosHoy}
          icon={ScanLine}
          label="Accesos validados hoy"
          value={kpis?.accesosValidadosHoy ?? 0}
          hint="Ir a Recepción"
        />
        <KpiCard
          testId={DASHBOARD_KPIS.eventosProximos}
          icon={CalendarClock}
          label="Eventos próximos"
          value={kpis?.eventosProximos ?? 0}
          hint="Ver todos"
          onClick={() => navigate("/admin/eventos")}
        />
        <KpiCard
          testId={DASHBOARD_KPIS.alertas}
          icon={AlertTriangle}
          label="Alertas"
          value={(kpis?.alertas || []).length}
          hint={(kpis?.alertas || []).length > 0 ? "Revisar eventos con inconsistencias" : "Sin alertas"}
          onClick={(kpis?.alertas || []).length > 0 ? () => navigate("/admin/eventos") : undefined}
          tone={(kpis?.alertas || []).length > 0 ? "warning" : "default"}
        />
        <KpiCard
          testId="dashboard-notificaciones"
          icon={Ticket}
          label="Centro de notificaciones"
          value="→"
          hint="Redactar notificación"
          onClick={() => navigate("/admin/notificaciones")}
        />
      </section>

      <section className="flex flex-col gap-5">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <span className="overline text-accent">Agenda</span>
            <h2 className="mt-1 font-heading text-2xl">Próximos eventos</h2>
          </div>
        </div>
        <div data-testid={ADMIN_DASHBOARD.proximosList} className="rounded-2xl border hairline bg-card divide-y divide-border overflow-hidden">
          {proximos.length === 0 && <div className="px-6 py-6 text-sm text-muted-foreground">Sin eventos próximos registrados.</div>}
          {proximos.map((ev) => (
            <button
              key={ev.id}
              type="button"
              data-testid={ADMIN_DASHBOARD.proximosItem(ev.id)}
              onClick={() => navigate(`/admin/eventos/${ev.id}/editar`)}
              className="w-full flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 px-5 md:px-6 py-4 text-left hover:bg-muted/40 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="font-heading text-lg leading-tight truncate">{ev.nombre}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatearFechaCortaISO(ev.fecha) || "—"} · {ev.hora || "—"} · {ev.salon || "—"}
                </p>
              </div>
              <EstadoEventoBadge estado={ev.estado} />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
